import { WithdrawRequest, WithdrawRequestStatus } from "./withdraw.types";

export const formatCurrency = (value: number) =>
  new Intl.NumberFormat("th-TH").format(value || 0);

export const formatDateTime = (value?: string) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("th-TH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toFiniteNumber = (value: any, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

export const normalizeWithdrawStatus = (
  value: any,
): WithdrawRequestStatus => {
  const status = String(value || "")
    .trim()
    .toUpperCase();

  if (
    status === "PENDING" ||
    status === "SUCCESS" ||
    status === "FAILED" ||
    status === "CANCELED"
  ) {
    return status;
  }

  if (status === "IN_QUEUE") return "PENDING";
  return "PENDING";
};

export const getWithdrawNetAmount = (
  item: Pick<WithdrawRequest, "amount" | "feeAmount">,
) => Math.max(toFiniteNumber(item.amount) - toFiniteNumber(item.feeAmount), 0);

export const normalizeWithdrawRequest = (raw: any): WithdrawRequest => {
  const bankSource =
    raw?.bankInfo || raw?.bankAccount || raw?.account || raw?.bank || {};
  const bankPayload =
    bankSource && typeof bankSource === "object" ? bankSource : {};
  const id = String(raw?._id || raw?.id || raw?.ref || "").trim();
  const code = String(
    raw?.code ||
      raw?.withdrawCode ||
      raw?.transactionCode ||
      raw?.reference ||
      raw?.ref ||
      "",
  ).trim();
  const createdAt = String(
    raw?.createdAt || raw?.created_at || raw?.date || raw?.time || "",
  ).trim();

  return {
    id: id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    code: code || undefined,
    userId: raw?.userId,
    walletId: raw?.walletId,
    businessId: raw?.businessId,
    message: String(raw?.message || raw?.note || "").trim() || undefined,
    bankName:
      String(
        raw?.bankName ||
          raw?.bank_name ||
          (typeof raw?.bank === "string" ? raw.bank : "") ||
          bankPayload?.bankName ||
          bankPayload?.bank_name ||
          bankPayload?.name ||
          "",
      ).trim() || undefined,
    bankAccountName:
      String(
        raw?.bankAccountName ||
          raw?.bank_account_name ||
          raw?.accountName ||
          raw?.account_name ||
          bankPayload?.bankAccountName ||
          bankPayload?.accountName ||
          bankPayload?.account_name ||
          "",
      ).trim() || undefined,
    bankAccountNo:
      String(
        raw?.bankAccountNo ||
          raw?.bank_account_no ||
          raw?.bankAccountNumber ||
          raw?.accountNo ||
          raw?.accountNumber ||
          bankPayload?.bankAccountNo ||
          bankPayload?.accountNo ||
          bankPayload?.accountNumber ||
          "",
      ).trim() || undefined,
    amount: toFiniteNumber(raw?.amount ?? raw?.amountGross),
    feeAmount: toFiniteNumber(raw?.feeAmount ?? raw?.fee_amount),
    balanceBefore: toFiniteNumber(
      raw?.balanceBefore ?? raw?.balance_before ?? raw?.before,
    ),
    balanceAfter: toFiniteNumber(
      raw?.balanceAfter ?? raw?.balance_after ?? raw?.after,
    ),
    transactionType: raw?.transactionType,
    status: normalizeWithdrawStatus(raw?.status),
    createdAt: createdAt || new Date().toISOString(),
    updatedAt: raw?.updatedAt,
  };
};

export const normalizeWithdrawRequests = (response: any) => {
  const payload = response?.data ?? response;
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.rows)
        ? payload.rows
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

  return list.map(normalizeWithdrawRequest);
};

const monthKey = (value: string) => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const monthLabel = (key: string) => {
  const [year, month] = key.split("-");
  return `${month}/${year}`;
};

export const buildMonthlyNetTotals = (
  history: Array<{
    createdAt: string;
    amount?: number;
    feeAmount?: number;
    amountNet?: number;
  }>,
) => {
  if (!history.length) return [];

  const grouped: Record<string, { month: string; netTotal: number }> = {};
  history.forEach((item) => {
    if (!item?.createdAt) return;
    const key = monthKey(item.createdAt);
    const amountNet = Number.isFinite(Number(item.amountNet))
      ? Number(item.amountNet)
      : Math.max(
          toFiniteNumber(item.amount) - toFiniteNumber(item.feeAmount),
          0,
        );

    if (!grouped[key]) grouped[key] = { month: monthLabel(key), netTotal: 0 };
    grouped[key].netTotal += amountNet;
  });

  return Object.keys(grouped)
    .sort()
    .slice(-6)
    .map((key) => grouped[key]);
};
